// City art sizing (docs/ART-DIRECTION.md §1, §2). Every part of a city marker (node diamond or capital octahedron,
// owner rim or capital halo, sigil, HP and capture arcs, territory glow, building ring) is authored in CSS pixels and
// drawn at k render units per CSS pixel:
//
//   k = min(worldPerCssPixel, cap)
//
// worldPerCssPixel is the frame's zoom (render units per CSS pixel at the surface under the camera). Zoomed in, k
// follows it, so a marker keeps its screen size. World-sized rings grew eightfold at the closest zoom and
// "overshadowed everything" (user, 2026-09-28). The cap is fixed per city until the art is retuned. Past the full-size
// zoom, or sooner where neighbours crowd, the art keeps its world size and shrinks with the globe, so the building
// rings of adjacent cities never overlap. Caps come from each city's nearest neighbour, found on a uniform 3D grid (each
// city examines its own cell and the 26 around it). No minimum: a floor would bring back overlaps in the closest pairs.
//
// Every size and brightness is a tuning knob (@taxwar/tuning, ROADMAP D33): cityArtOf() turns the values into the art
// the layers draw, and the tuner retunes a running war through the same path. The vertex shaders apply the sizing rule
// on the GPU (CITY_ART_GLSL); the building ring applies it on the CPU (cityArtWorldPerPx).

import { Vector3 } from 'three'
import { InvariantViolation, type MapCityView, type MatchSetupView } from '@taxwar/net'
import type { TuningValues } from '@taxwar/tuning'
import { latLonToVector3 } from '../geo/globeSpace'

export type CityArtKind = 'city' | 'capital'

/** One kind of marker, in CSS px. */
export interface CityArtPx {
  /** Node octahedron: half width across the surface, half height along the normal, and its gap above the surface. */
  readonly nodeHalfWidth: number
  readonly nodeHalfHeight: number
  readonly nodeHover: number
  /** Owner rim ring (the capital's halo, ART-DIRECTION §2.3): outer radius and stroke. */
  readonly rimRadius: number
  readonly rimStroke: number
  /** Outer radius of the HP arc; the capture arc runs inside it. */
  readonly statusRadius: number
  readonly glowRadius: number
}

/** Brightness multipliers (ART-DIRECTION §2.5 glow discipline: ambient parts dim, HP arcs brightest). */
export interface CityArtBrightness {
  /** Node octahedrons: their fill, and the extra glow toward their outline. */
  readonly nodeFill: number
  readonly nodeEdge: number
  readonly rim: number
  readonly sigil: number
  /** The filled part of the HP and capture arcs, and their unfilled track. */
  readonly hp: number
  readonly track: number
  readonly glow: number
  readonly edges: number
}

export interface CityArt {
  readonly kinds: Readonly<Record<CityArtKind, CityArtPx>>
  /** HP and capture arcs: the same strokes and gap around every kind. */
  readonly status: { readonly hpStroke: number; readonly captureStroke: number; readonly gap: number }
  /** Capital sigil (ART-DIRECTION §2.3): a star of this radius floating `gap` above the octahedron's tip. */
  readonly sigil: { readonly radius: number; readonly gap: number }
  /** Buildings stand on a ring this far from their city's centre, outside the status arcs. */
  readonly buildingRingPx: number
  /** The zoom (render units per CSS px) up to which city art holds its screen size. */
  readonly fullSizeWorldPerCssPx: number
  /** A city's building ring stays within this fraction of the arc to its nearest neighbour. */
  readonly crowding: number
  readonly brightness: CityArtBrightness
}

/** The city art a set of tuning values describes. */
export function cityArtOf(values: TuningValues): CityArt {
  return {
    kinds: {
      city: {
        nodeHalfWidth: values['city.nodeHalfWidth'],
        nodeHalfHeight: values['city.nodeHalfHeight'],
        nodeHover: values['city.nodeHover'],
        rimRadius: values['city.rimRadius'],
        rimStroke: values['city.rimStroke'],
        statusRadius: values['city.statusRadius'],
        glowRadius: values['city.glowRadius'],
      },
      capital: {
        nodeHalfWidth: values['capital.nodeHalfWidth'],
        nodeHalfHeight: values['capital.nodeHalfHeight'],
        nodeHover: values['capital.nodeHover'],
        rimRadius: values['capital.rimRadius'],
        rimStroke: values['capital.rimStroke'],
        statusRadius: values['capital.statusRadius'],
        glowRadius: values['capital.glowRadius'],
      },
    },
    status: { hpStroke: values['status.hpStroke'], captureStroke: values['status.captureStroke'], gap: values['status.gap'] },
    sigil: { radius: values['capital.sigilRadius'], gap: values['capital.sigilGap'] },
    buildingRingPx: values['buildings.ringRadius'],
    fullSizeWorldPerCssPx: values['sizing.fullSizeZoom'],
    crowding: values['sizing.crowding'],
    brightness: {
      nodeFill: values['brightness.nodeFill'],
      nodeEdge: values['brightness.nodeEdge'],
      rim: values['brightness.rim'],
      sigil: values['brightness.sigil'],
      hp: values['brightness.hp'],
      track: values['brightness.track'],
      glow: values['brightness.glow'],
      edges: values['brightness.edges'],
    },
  }
}

/**
 * Flat parts are drawn on quads this much larger than their outer radius, so a stroke's antialiased edge is never
 * clipped (networkShaders.ts ringCoverage). Not a tuning knob: it only has to cover the antialiasing.
 */
export const CITY_ART_AA_MARGIN_PX = 2

const XYZ = 3
/** Unit vectors span [−1, 1] on each axis. */
const UNIT_SPAN = 2
/** No two points on the unit sphere are farther apart than this arc. */
const MAX_ARC_RAD = Math.PI
const NEIGHBOUR_CELL_OFFSETS = [-1, 0, 1] as const

export function cityArtKind(city: MapCityView): CityArtKind {
  return city.capital ? 'capital' : 'city'
}

/** Render units per CSS px a city's art is drawn at this frame; the GPU twin is CITY_ART_GLSL. */
export function cityArtWorldPerPx(worldPerCssPixel: number, cap: number): number {
  return Math.min(worldPerCssPixel, cap)
}

/**
 * Vertex-shader chunk placing city art, the twin of cityArtWorldPerPx. The instance matrix is the city's surface frame
 * at its layer's altitude; `local` is in CSS px once that matrix is applied (its axes may scale unit geometry to px).
 * `liftPx` raises the part along the surface normal.
 */
export const CITY_ART_GLSL = /* glsl */ `
uniform float uWorldPerPx;
attribute float aMaxWorldPerPx;

vec4 cityArtWorldPosition(vec3 local, float liftPx) {
  float k = min(uWorldPerPx, aMaxWorldPerPx);
  vec4 world = instanceMatrix * vec4(local * k, 1.0);
  world.xyz += normalize(instanceMatrix[3].xyz) * (liftPx * k);
  return world;
}
`

/**
 * One cap (render units per CSS px) per CityIndex: at most the full-size zoom, less where the nearest city is close
 * enough that the building rings would meet.
 */
export function cityArtCapsOf(setup: MatchSetupView, art: CityArt): Float32Array {
  const cities = setup.cities
  const count = cities.length
  if (count < 2) throw new InvariantViolation(`a war map needs at least two cities, got ${count}`)
  // Nearest-neighbour arc at which the spacing cap reaches the full-size zoom; farther neighbours never bind it.
  const referenceSpacingRad = Math.min(MAX_ARC_RAD, (art.buildingRingPx * art.fullSizeWorldPerCssPx) / art.crowding)
  const cell = 2 * Math.sin(referenceSpacingRad / 2)
  const cellsPerAxis = Math.ceil(UNIT_SPAN / cell) + 1

  const points = new Float64Array(count * XYZ)
  const cells = new Int32Array(count * XYZ)
  const buckets = new Map<number, number[]>()
  const point = new Vector3()
  for (let i = 0; i < count; i++) {
    const city = cities[i] as MapCityView
    const o = i * XYZ
    latLonToVector3(city.lat, city.lon, 1, point).toArray(points, o)
    for (let axis = 0; axis < XYZ; axis++) cells[o + axis] = Math.floor(((points[o + axis] as number) + 1) / cell)
    const key = cellKey(cells[o] as number, cells[o + 1] as number, cells[o + 2] as number, cellsPerAxis)
    const bucket = buckets.get(key)
    if (bucket === undefined) buckets.set(key, [i])
    else bucket.push(i)
  }

  const caps = new Float32Array(count)
  for (let i = 0; i < count; i++) {
    const o = i * XYZ
    let best = cell * cell
    for (const dx of NEIGHBOUR_CELL_OFFSETS) {
      for (const dy of NEIGHBOUR_CELL_OFFSETS) {
        for (const dz of NEIGHBOUR_CELL_OFFSETS) {
          const bucket = buckets.get(cellKey((cells[o] as number) + dx, (cells[o + 1] as number) + dy, (cells[o + 2] as number) + dz, cellsPerAxis))
          if (bucket === undefined) continue
          for (const j of bucket) {
            if (j === i) continue
            const p = j * XYZ
            const x = (points[o] as number) - (points[p] as number)
            const y = (points[o + 1] as number) - (points[p + 1] as number)
            const z = (points[o + 2] as number) - (points[p + 2] as number)
            best = Math.min(best, x * x + y * y + z * z)
          }
        }
      }
    }
    if (best === 0) throw new InvariantViolation(`city ${i} shares coordinates with another city`)
    const nearestArc = 2 * Math.asin(Math.min(1, Math.sqrt(best) / 2))
    caps[i] = Math.min(art.fullSizeWorldPerCssPx, (nearestArc * art.crowding) / art.buildingRingPx)
  }
  return caps
}

/** Out-of-range cells (one past either face) map to keys no city occupies. */
function cellKey(x: number, y: number, z: number, cellsPerAxis: number): number {
  const span = cellsPerAxis + 2
  return ((x + 1) * span + (y + 1)) * span + (z + 1)
}
